/**
 * SkillFocusPanel.jsx — Feb 09 2026, Lane 2 follow-up.
 *
 * Slots into CoverageTracker beside the existing SkillsPanels (chip
 * treatment for Transferable + Learning). Skill Focus is a single-
 * select at generator time drawn from the same 7-item transferable-
 * skill picker, so its natural home is between the Transferable Skills
 * and Learning Skills panels.
 *
 * Visual treatment: matches SkillsPanel's card wrapper + header, but
 * renders bars scaled to `total_activities` (the Phase-2 canonical
 * denominator) — "absolute bars" per the founder brief. Bars are the
 * same shape/colour the old Home CoverageBars component used, so a
 * teacher who saw the Home bars pre-Lane 2 gets the same visual on
 * Coverage Tracker post-Lane 2.
 *
 * Canonical labels: `coverage.by_skill_focus` is already keyed by
 * canonical transferable-skill name (see `canonical_transferable` in
 * `backend/routes/coverage.py`), so no client-side normalisation is
 * needed. We render the full list of 7 canonical skills (including
 * zero-coverage ones) so the spine stays visible, matching the Rings
 * card discipline on Home.
 */

// Canonical 7 transferable skills. Mirrors backend
// `skill_in_the_wild.LIBRARY_NAMES` but written in the "and" canonical
// form that `canonical_transferable` on the backend normalises to.
const CANONICAL_SKILL_FOCUS_LABELS = [
  "Critical Thinking and Problem Solving",
  "Innovation, Creativity, and Entrepreneurship",
  "Self-Directed Learning",
  "Collaboration",
  "Communication",
  "Global Citizenship and Sustainability",
  "Digital Literacy",
];

// Legacy library-form aliases that older activities may still carry.
const _ALIAS = {
  "Critical Thinking & Problem Solving": "Critical Thinking and Problem Solving",
  "Innovation, Creativity & Entrepreneurship": "Innovation, Creativity, and Entrepreneurship",
  "Global Citizenship & Sustainability": "Global Citizenship and Sustainability",
};

const _canonicalCount = (byName, canonical) => {
  if (!byName) return 0;
  let n = Number(byName[canonical]) || 0;
  for (const [aliasFull, canon] of Object.entries(_ALIAS)) {
    if (canon === canonical && byName[aliasFull] != null) {
      n += Number(byName[aliasFull]) || 0;
    }
  }
  return n;
};

const SkillFocusPanel = ({ coverage }) => {
  const byName = coverage?.by_skill_focus || {};
  const total = Number(coverage?.total_activities) || 0;
  const denom = Math.max(1, total);

  // Build absolute-bar rows. Sort descending by count so heavy usage
  // reads first, ties broken by canonical order (already alphabetical
  // in `CANONICAL_SKILL_FOCUS_LABELS` for stable output).
  const rows = CANONICAL_SKILL_FOCUS_LABELS.map((name) => ({
    name,
    count: _canonicalCount(byName, name),
  })).sort((a, b) => b.count - a.count);

  return (
    <section
      data-testid="tracker-skill-focus"
      className="ae-card p-6 md:p-7"
    >
      <header className="mb-5">
        <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
          Skill Focus
        </h3>
        <p className="mt-1 text-xs text-[#4a6984]/70 font-medium">
          Which transferable skill you chose as the focus when generating each lesson.
        </p>
      </header>
      <div className="space-y-3.5">
        {rows.map((r) => {
          const pct = Math.max(0, Math.min(100, (r.count / denom) * 100));
          return (
            <div
              key={r.name}
              data-testid={`tracker-skill-focus-row-${r.name}`}
              className="min-w-0"
            >
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="font-semibold text-sm text-[#4a6984] truncate">
                  {r.name}
                </span>
                <span
                  className="font-bold text-xs text-[#1c2d3d] tabular-nums shrink-0"
                  data-testid={`tracker-skill-focus-count-${r.name}`}
                >
                  {r.count}/{total}
                </span>
              </div>
              <div
                className="h-2 rounded-full bg-[#e5eaef] overflow-hidden"
                role="progressbar"
                aria-label={`${r.name}: ${r.count} of ${total}`}
                aria-valuenow={r.count}
                aria-valuemin={0}
                aria-valuemax={total}
              >
                <div
                  className="h-full rounded-full bg-[#96ded1] transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SkillFocusPanel;
